import { targetHandoffMs } from "./targetCharge";

// Het feestje.
//
// Dit hoort bij niets in de cijfers. Alle andere animaties komen ergens
// vandaan - een verkoop, een record, het target - maar deze zet je zelf aan,
// op het moment dat het bord toch al vol staat en er iemand bij het
// toetsenbord kan. Shift+8 op het dashboard, of typ "feest" op de tv.
//
// Er zijn vijf animaties en ze lopen over elkaar heen:
//
//   party-dance  - de kaarten zelf gaan op de maat mee. Dat loopt via de
//                  klasse op de dashboard-div, net als de meter en het
//                  podium, want dit beweegt de echte onderdelen en niet iets
//                  wat er overheen ligt.
//   party-blocks - vlakke banen kleur die schuin over het beeld schuiven.
//   party-hype   - een balk onderin die aanmoedigingen doorbladert.
//   party-lights - lichtbundels van boven die heen en weer zwaaien en op de
//                  tel mee knipperen. Vlakke kleur, geen gloed: het
//                  dashboard blijft plat, ook als het feest is.
//   party-cannons - confetti die vanuit beide hoeken onderin omhoog wordt
//                  geschoten en in een boog weer naar beneden valt.
//
// Daartussen valt de gewone confetti van boven; die had het dashboard al.
//
// De maat. Alles hier telt in slagen, zodat beeld en muziek bij elkaar
// blijven: het wippen van een kaart, het doorschuiven van een baan en het
// wisselen van een regel vallen allemaal op een slag. Komt er ooit een ander
// nummer in public/sounds/feest.mp3, dan zijn dit de twee getallen die mee
// moeten - en --fx-party-beat en --fx-party-ms in animations.css, die
// dezelfde waarden gebruiken voor wat de CSS zelf aanstuurt.

// 120 slagen per minuut.
export const partyBeatMs = 500;

// Zo lang als public/sounds/feest.mp3 duurt. Het dansen loopt precies met de
// muziek mee: houdt het beeld eerder op, dan speelt het nummer tegen een
// stilstaand dashboard uit. Nu Celebration van Kool & The Gang, 16,9s
// (gemeten aan de mp3-frames).
export const partyMs = 16900;

// De banen en de balk stoppen wat eerder, zodat het beeld tot rust komt
// terwijl het nummer uitfadet in plaats van allebei tegelijk af te breken.
export const partyBlocksMs = 28 * partyBeatMs; // 14,0s
export const partyHypeAt = 2 * partyBeatMs; // 1,0s
export const partyLineMs = 5 * partyBeatMs; // 2,5s per regel

// Wat er onderin komt te staan. Eén regel per beurt, in deze volgorde, en
// daarna is de balk weg. Hoe lang party-hype duurt volgt hieruit, dus een
// regel erbij maakt hem vanzelf langer - tot aan het eind van het nummer.
//
// Altijd dezelfde, elke dag. Zes regels en niet meer: vanaf 1s zes beurten
// van 2,5s is 16s, en het nummer duurt 16,9s.
export const partyLines = [
  "FEEST!",
  "CELEBRATE GOOD TIMES",
  "WAT EEN TEAM",
  "BIJNA WEEKEND!",
  "DIT BORD STAAT TE DANSEN",
  "TOT DE VOLGENDE VERKOOP",
];

export const partyHypeMs = partyLines.length * partyLineMs;

// De lichtshow loopt het hele nummer mee en dooft met de uitfade weg.
export const partyLightsMs = 32 * partyBeatMs; // 16,0s

// Eén salvo uit de kanonnen: de snippers gaan binnen partyCannonSpread
// seconden de lucht in en vliegen elk tussen de 2,2 en 3,2 seconden. De `ms`
// hieronder is het langste van die twee bij elkaar, plus wat marge.
export const partyCannonSpread = 0.35;
export const partyCannonMs = 3700;

// Wanneer er iets knalt, in slagen na het begin. De kanonnen openen het
// feestje als het nummer goed hoorbaar is (de infade duurt 4,5s, zie
// samples.js), en komen daarna om de drie seconden terug; de laatste landt
// net voor het nummer uit is. De regen van boven valt er twee keer tussen.
// Op een trage Pi zijn het vanzelf minder snippers per salvo, zie
// partyCannonBits in utils/renderTier.js.
const beats = (list) => list.map((beat) => beat * partyBeatMs);
const cannonsAt = beats([4, 10, 16, 22]);
const burstsAt = beats([7, 19]);

// Het feestje zelf, vanaf `at` ms na het begin van het groepje. Los van
// partyGroup, zodat het target het achter zijn eigen kaart kan hangen.
//
// party-blocks zit er niet in: schuine banen en draaiende bundels door elkaar
// zijn twee soorten strepen, en dan leest geen van beide nog. De lichtshow is
// de mooiste van de twee; de banen staan los in het lab.
//
// ms: hoe lang het dansen en de lichtshow duren, als het niet het gewone
// nummer is (het target heeft een eigen, langer nummer). quiet: het dansen
// zonder zijn eigen nummer, omdat er al een ander loopt.
function partyEvents({
  at = 0,
  hypeAt = partyHypeAt,
  lines = null,
  cannons = cannonsAt,
  bursts = burstsAt,
  ms = null,
  quiet = false,
} = {}) {
  const dance = { ...(ms ? { ms } : {}), ...(quiet ? { quiet } : {}) };
  const lights = ms ? { ms: ms - (partyMs - partyLightsMs) } : {};
  return [
    ["party-dance", dance, at],
    ["party-lights", lights, at],
    ["party-hype", lines ? { lines } : {}, at + hypeAt],
    ...cannons.map((time) => ["party-cannons", {}, at + time]),
    ...bursts.map((time) => ["confetti-burst", { count: 0.9 }, at + time]),
  ];
}

// Alles wat bij het feestje hoort, als lijst van [animatie, gegevens, tijd].
// Dezelfde vorm als saleGroup in hooks/useSaleEvents.js, dus de animatiemotor
// speelt het af als één groepje.
export function partyGroup() {
  return partyEvents();
}

// Het team tikt de 100% aan. Dat is één keer per maand, en dan mag het ook
// echt feest zijn. Drie bedrijven:
//
//   1. de aanloop: het beeld wordt donker en de meter loopt midden in beeld
//      van 0 naar 100 (target-charge, zie ./targetCharge.js), zonder muziek
//   2. op 100 kleurt de boog regenboog en komt het nummer zacht op; de meter
//      zoomt naar voren weg terwijl "GEHAALD" uit hetzelfde midden opkomt, en
//      op datzelfde moment gaat het hele dashboard los: het dansen, de
//      lichtshow, en op de klap van het nummer de kanonnen
//   3. als de kaart weg is, komt de balk onderin met de regels, tot het
//      nummer uit is
//
// Het target heeft een eigen nummer, twee keer zo lang als dat van het
// feestje: public/sounds/feest-target.mp3, hetzelfde begin van Celebration
// maar dan doorgespeeld (31,3s, gemeten aan de mp3). Het feestje met
// Shift+8 houdt het korte.
//
// Het nummer begint pas als de meter de 100 geraakt heeft: onder een meter
// die nog loopt hoort geen muziek, dan is het feest al begonnen voor het
// gehaald is. Het komt zacht op (de infade staat bij "target-song" in
// audio/samples.js) en de klap ("come on!", 3,0s in het bestand, gemeten aan
// de luidheid van de opname) valt terwijl "GEHAALD" staat. Daar gaat ook het
// eerste salvo af.
//
// Precies een slag voor de kaart, en niet op de 100 zelf: het dansen begint
// met de kaart, en zo valt het op de maat van het nummer (120 BPM, een slag
// is partyBeatMs). De kaart en het dansen spelen zonder eigen geluid (quiet):
// twee nummers over elkaar is geen feest maar lawaai. Het nummer draait onder
// target-song, dat niets laat zien.
//
// Confetti om de drie seconden: salvo's uit de kanonnen, en daartussen een
// bui van boven, tot vlak voor het nummer uit is. Dit is het grootste moment
// van de maand, dus hier mag het een keer vol hangen; op een trage Pi zijn
// het er vanzelf minder, want het aantal snippers komt van de kwaliteitstrap
// (zie utils/renderTier.js).
//
// Onder het feest zakt het beeld een tikje weg (target-party-dim): veel
// lichter dan het donker bij een verkoop, net genoeg dat de lichtshow en de
// confetti ervan loskomen. Hij ligt onder de kaart, dus zolang "GEHAALD"
// staat zie je hem niet; daarna blijft hij tot het nummer uit is.
export const targetCardAt = targetHandoffMs;
export const targetPartyAt = targetCardAt;
const targetCardMs = 9500; // de `ms` van target-hit in registry.js

export const targetSongMs = 31300; // de lengte van feest-target.mp3
const targetSongHitMs = 3000;
export const targetSongAt = targetCardAt - partyBeatMs; // 0,2s na de 100
// Het feest loopt vanaf de kaart tot het nummer uit is.
export const targetPartyMs = targetSongAt + targetSongMs - targetPartyAt; // 30,8s
// De klap, in slagen na de kaart: daar gaat het eerste salvo af.
const targetHitBeat = (targetSongAt + targetSongHitMs - targetPartyAt) / partyBeatMs; // 5

// De balk komt pas als de kaart weg is en is klaar als het nummer uitfadet:
// zeven regels van 2,5s, van 10s tot 27,5s. Altijd dezelfde, elke dag.
// "TARGET GEHAALD!" staat er niet in, dat zegt de kaart al.
export const targetLines = [
  "HET UITJE GAAT DOOR",
  "CELEBRATE GOOD TIMES",
  "WAT EEN TEAM",
  "100% GEHAALD",
  "BIJNA WEEKEND!",
  "IEDEREEN BEDANKT",
  "OP NAAR DE VOLGENDE MAAND",
];

// Om de zes slagen een salvo, drie slagen verschoven een bui van boven. Het
// laatste salvo landt voor het nummer uit is, de laatste bui ook.
const every = (from, to, step) =>
  Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, i) => from + i * step);

export function targetPartyGroup(amount) {
  return [
    ["target-charge", {}, 0],
    ["target-song", {}, targetSongAt],
    // Voor de kaart in de lijst, dan ligt hij er in de animatielaag onder.
    ["target-party-dim", {}, targetPartyAt],
    ["target-hit", { amount, quiet: true }, targetCardAt],
    ...partyEvents({
      at: targetPartyAt,
      hypeAt: targetCardMs + partyBeatMs,
      lines: targetLines,
      cannons: beats(every(targetHitBeat, 53, 6)),
      bursts: beats(every(targetHitBeat + 3, 44, 6)),
      ms: targetPartyMs,
      quiet: true,
    }),
  ];
}
